import { defer, EMPTY, expand, finalize, from, map, Observable, reduce, share } from "rxjs"
import { OkxRestClient, OkxWebsocketClient } from "../../../core/market"
import { createStringNumberMap, mergeStringNumberMaps, parseFloatSafe } from "../../../core/func"
import { AccountBalance, InstrumentType, WsPosition } from "libs/okx-api/src"

const requestMap = new Map<OkxRestClient, Observable<AccountBalance[]>>()

export class OkxBalanceRestProvider {

  constructor(private rest: OkxRestClient) {}

  private createBalanceRequest() {
    let req = requestMap.get(this.rest)
    if (!req) {
      req = from(this.rest.getBalance()).pipe(
        finalize(() => requestMap.delete(this.rest)),
        share()
      )
      requestMap.set(this.rest, req)
    }
    return req
  }

  /**
   * @deprecated
   */
  requestBalance() {
    return this.createBalanceRequest().pipe(
      map(data => parseFloatSafe(data[0].totalEq)),
    )
  }

  requestBalances() {
    return this.createBalanceRequest().pipe(
      map(data => createStringNumberMap(data[0].details, 'ccy', 'cashBal')),
    )
  }

  /**
   * @deprecated
   */
  requestPnl(startTime: number) {
    const params = {
      instType: 'SWAP' as InstrumentType,
      before: startTime.toString(),
      limit: '100',
    }
    const req = defer(() => this.rest.getPositionsHistory(params))
    return req.pipe(
      expand(data => {
        if (data.length >= 100) {
          const lastTs = parseInt(data[data.length - 1].uTime)
          params.before = (lastTs + 1).toString()
          return req
        }
        return EMPTY
      }),
      map(data => data.reduce<number>((sum, row) => sum + parseFloatSafe(row.realizedPnl), 0)),
      reduce((acc, n) => acc + n, 0),
    )
  }

  requestPnls(startTime: number) {
    const params = {
      instType: 'SWAP' as InstrumentType,
      before: startTime.toString(),
      limit: '100',
    }
    const req = defer(() => this.rest.getPositionsHistory(params))
    return req.pipe(
      expand(data => {
        if (data.length >= 100) {
          const lastTs = parseInt(data[data.length - 1].uTime)
          params.before = (lastTs + 1).toString()
          return req
        }
        return EMPTY
      }),
      map(data => createStringNumberMap(data, 'instId', 'realizedPnl')),
      reduce((acc, data) => mergeStringNumberMaps(acc, data), {} as Record<string, number>)
    )
  }

}

export class OkxBalanceWebsocketProvider {

  constructor(private ws: OkxWebsocketClient) {}

  /**
   * @deprecated
   */
  realtimeBalance() {
    return new Observable<number>(subscriber => {
      const f = (data: any) => {
        if (data.arg?.channel === 'account' && data.data?.length) {
          const accountData = data.data[0]
          subscriber.next(parseFloatSafe(accountData.totalEq))
        }
      }

      this.ws.on('update', f)
      return () => this.ws.off('update', f)
    })
  }

  realtimeBalances() {
    return new Observable<Record<string, number>>(subscriber => {
      const f = (data: any) => {
        if (data.arg?.channel === 'account' && data.data?.length) {
          const accountData = data.data[0]
          if (accountData.details) {
            const balances: Record<string, number> = {}
            accountData.details.forEach((detail: any) => {
              balances[detail.ccy] = parseFloatSafe(detail.cashBal)
            })
            subscriber.next(balances)
          }
        }
      }

      this.ws.on('update', f)
      return () => this.ws.off('update', f)
    })
  }

  realtimePnlChange() {
    return new Observable<Record<string, number>>(subscriber => {
      const f = (data: any) => {
        if (data.arg?.channel === 'positions' && data.data?.length) {
          data.data.forEach((position: WsPosition) => {
            if (position.realizedPnl && parseFloatSafe(position.realizedPnl) !== 0) {
              subscriber.next({[position.instId]: parseFloatSafe(position.realizedPnl)})
            }
          })
        }
      }

      this.ws.on('update', f)
      return () => this.ws.off('update', f)
    })
  }

}
